import currencySymbolMap from 'currency-symbol-map';
import { formatNumberWithCommas } from "./formatNumberWithCommas";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";


export const formatToFixed = (value) => (Number(value) || 0).toFixed(2);

export const getCarrierRows = (services, role) => {
  if (!services || services.length === 0) return [];
  const isAdmin = role === 'admin';

  const rows = services.map((service, index) => ({
    id: index + 1,
    name: service.carrierName?.toUpperCase(),
    sales: formatNumberWithCommas(service.totalSales),
    ...(isAdmin && { totalRevenue: `$ ${formatNumberWithCommas(service.totalCarrierCost)}` }),
    ...(isAdmin && { totalProfit: `$ ${formatNumberWithCommas(service.totalProfit)}` }),
    totalCustomerPrice: `$ ${formatNumberWithCommas(service.totalCustomerPrice)}`,
    totalPackagesWeight: service?.totalPackagesWeight || "-",
    totalPackagesCount: service?.totalPackagesCount || "-",
  }));

  const totalRow = {
    id: 'Total',
    name: 'All Carriers',
    sales: formatNumberWithCommas(rows.reduce((sum, row) => sum + Number(row.sales.replace(/,/g, '')), 0)),
    ...(isAdmin && { totalRevenue: `$ ${formatNumberWithCommas(rows.reduce((sum, row) => sum + Number(row.totalRevenue?.replace(/[^\d.]/g, '') || 0), 0))}` }),
    ...(isAdmin && { totalProfit: `$ ${formatNumberWithCommas(rows.reduce((sum, row) => sum + Number(row.totalProfit?.replace(/[^\d.]/g, '') || 0), 0))}` }),
    totalCustomerPrice: `$ ${formatNumberWithCommas(rows.reduce((sum, row) => sum + Number(row.totalCustomerPrice.replace(/[^\d.]/g, '')), 0))}`,
    totalPackagesWeight: formatToFixed(rows.reduce((sum, row) => sum + Number(row.totalPackagesWeight || 0), 0)),
    totalPackagesCount: rows.reduce((sum, row) => sum + Number(row.totalPackagesCount || 0), 0),
  };

  return [...rows, totalRow];
};


export const getCarrierVersionRows = (services, role) => {
  let index = 1;
  const isAdmin = role === 'admin';
  return services?.flatMap((service) =>
    service?.carrierDetails?.map((detail) => {
      const originalCurrencyType = detail?.shipmentCharges?.currencyType;

      const shipmentCreatedAt = detail?.createdAt ? new Date(detail.createdAt) : null;
      const thresholdDate = new Date("2025-02-01T00:00:00");
      let currencySymbol;
      if (originalCurrencyType === 'BILLC') {
        if (shipmentCreatedAt >= thresholdDate) {
          currencySymbol = "$";
        } else currencySymbol = "€"
      } else {
        currencySymbol = (shipmentCreatedAt && shipmentCreatedAt >= thresholdDate)
          ? '$'
          : currencySymbolMap(originalCurrencyType);
      }
      return {
        id: index++,
        name: detail?.carrierVersion?.toUpperCase(),
        trackingNumber: detail?.trackingNumber || '-',
        ...(isAdmin && {
          revenue: detail?.shipmentCharges?.carrierCost
            ? `${currencySymbol || ""} ${formatNumberWithCommas(detail.shipmentCharges.carrierCost)}`
            : '-'
        }),
        ...(isAdmin && {
          profit: (detail?.shipmentCharges?.customerPrice !== undefined &&
            detail?.shipmentCharges?.carrierCost !== undefined)
            ? `${currencySymbol || ""} ${formatNumberWithCommas(
              (detail.shipmentCharges.customerPrice - detail.shipmentCharges.carrierCost).toFixed(2)
            )}`
            : '-'
        }),
        customerPrice: detail?.shipmentCharges?.customerPrice
          ? `${currencySymbol || ""} ${formatNumberWithCommas(detail.shipmentCharges.customerPrice)}`
          : '-',
        from: detail?.from?.toUpperCase() || '-',
        to: detail?.to?.toUpperCase() || '-',
        totalPackagesWeight: detail?.totalPackagesWeight || "-",
        packagesCount: detail?.packagesCount || "-",
        shipmentDate: detail?.shipmentDate
          ? new Intl.DateTimeFormat('en-GB', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
          }).format(new Date(detail.shipmentDate))
          : '-',
        createdAt: detail?.createdAt
          ? new Intl.DateTimeFormat('en-GB', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
          }).format(new Date(detail.createdAt))
          : '-',
        createdBy: detail?.createdBy || "-"
      };
    })
  );
}

const getConvertedAmount = (shipment) => {
  const originalAmount = parseFloat(shipment.amount || "0");
  let symbol;
  if (shipment.currencyType === "USD" || shipment.currencyType === "CAD") {
    symbol = '$';
  } else if (shipment.currencyType === "EUR") {
    symbol = '€';
  } else {
    symbol = '$';
  }
  return { amount: originalAmount, currencySymbol: symbol };
};

export const generatePDF = async ({ startDate, endDate, token, updated }) => {
  try {
    const start = startDate ? startDate.toISOString() : '';
    const end = endDate ? endDate.toISOString() : '';
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/admin/getLast15DaysShipments?startDate=${start}&endDate=${end}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const { success, shipments } = await response.json();

    if (shipments.length === 0) {
      toast.error("No records found.", {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }
    if (!success) {
      console.error("Failed to fetch shipments");
      return;
    }

    const sortedShipments = shipments.sort((a, b) =>
      new Date(b.createdAt) - new Date(a.createdAt)
    );

    const pdfDoc = await PDFDocument.create();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

    // Define page size and initialize row variables
    const pageSize = [950, 600];
    let currentPage = pdfDoc.addPage(pageSize);
    const rowHeight = 25;
    const columnWidths = [130, 65, 65, 90, 75, 120, 90, 90, 80, 80];
    let startY = 400;

    const drawHeader = (page) => {
      const headers = updated
      ? ["Tracking Number", "Sender", "Destination", "Shipment Date", "Carrier Name", "Declared Weight (KG)","Carrier Weight", "No of Packages", "Amount", "Created At" ]
      : ["Tracking Number", "Sender", "Destination", "Shipment Date", "Carrier Name", "Declared Weight (KG)", "No of Packages", "Amount", "Created At", "Created By"];

      let x = 30;
      headers.forEach((header, i) => {
        page.drawRectangle({
          x,
          y: startY,
          width: columnWidths[i],
          height: rowHeight,
          color: rgb(0.8, 0.9, 1),
          borderColor: rgb(0, 0, 0),
          borderWidth: 1,
        });
        page.drawText(header, { x: x + 5, y: startY + 5, size: 10, font, color: rgb(0, 0, 0) });
        x += columnWidths[i];
      });
      startY -= 0;
    };

    const addNewPage = () => {
      currentPage = pdfDoc.addPage(pageSize);
      startY = 550; // Reset startY for the new page
      drawHeader(currentPage); // Redraw the header on the new page
    };

    // Draw the title and background for the title section on the first page
    const backgroundColor = rgb(0 / 255, 37 / 255, 87 / 255);
    currentPage.drawRectangle({
      x: 28,
      y: 550,
      width: 833,
      height: 47,
      color: backgroundColor,
    });

    // Title text
    const titleText = "ParcelAB INTEGRATION";
    currentPage.drawText(titleText, {
      x: 310,
      y: 568,
      size: 25,
      font,
      color: rgb(1, 1, 1),
    });

    const invoiceText = 'INVOICE DETAILS';
    currentPage.drawText(invoiceText, {
      x: 300,
      y: 440,
      size: 20,
      font,
      color: rgb(0, 0, 0),
    });

    // Left and right side information
    const leftInfo = [
      "A&H CHANNELS",
      "M. EL HARRUCHI ANASS",
      "18 AVENUE THIERS",
      "06000 NICE",
      "FRANCE",
    ];
    let leftY = 533;
    leftInfo.forEach((line) => {
      currentPage.drawText(line, { x: 30, y: leftY, size: 12, font, color: rgb(0, 0, 0) });
      leftY -= 15;
    });

    const rightInfo = [
      `Email: anass@ehadistribution.com`,
      `Invoice Date: ${new Date().toLocaleDateString()}`,
      `Phone: +33 123 456 789`,
    ];
    let rightY = 533;
    rightInfo.forEach((line) => {
      currentPage.drawText(line, { x: 650, y: rightY, size: 12, font, color: rgb(0, 0, 0) });
      rightY -= 15;
    });

    // Draw table headers on the first page
    drawHeader(currentPage);

    // Populate rows with API data (for the first page)
    sortedShipments.slice(0, 16).forEach((shipment, index) => {
      const y = startY - rowHeight * (index + 1);
      let x = 30;

      const { amount, currencySymbol } = getConvertedAmount(shipment);

      const rowData = [
        String(shipment?.trackingNumber ?? "N/A"),
        String(shipment?.senderAddress?.countryCode?.toUpperCase() ?? "N/A"),
        String(shipment?.destination?.address?.countryCode?.toUpperCase() ?? "N/A"),
        String(shipment?.shipmentDate ?? "N/A"),
        String(shipment?.carrierName?.toUpperCase() ?? "N/A"),
        String(shipment?.weight ?? "0"),
        ...(updated ? [String(shipment?.carrierWeight ?? "-")] : []),  // Carrier Weight will be included if updated is true
        String(shipment?.noOfPackages ?? "0"),
        `${currencySymbol} ${amount}`,
        String(shipment?.createdAt.split('T')[0] ?? "N/A"),
        ...(updated ? [] : [String(shipment?.createdBy ?? "N/A")]), // Carrier Weight if updated is true, else Created By
      ];

      rowData.forEach((cell, colIndex) => {
        currentPage.drawRectangle({
          x,
          y,
          width: columnWidths[colIndex],
          height: rowHeight,
          borderColor: rgb(0, 0, 0),
          borderWidth: 1,
        });
        currentPage.drawText(cell, { x: x + 5, y: y + 5, size: 8, font, color: rgb(0, 0, 0) });
        x += columnWidths[colIndex];
      });

      startY -= 0; // Update for next row (if needed)
    });

    // Check if there are more shipments to add and create additional pages if needed
    let remainingShipments = sortedShipments.slice(16);

    while (remainingShipments.length > 0) {
      addNewPage(); // Add a new page
      remainingShipments.slice(0, 20).forEach((shipment, index) => {
        const y = startY - rowHeight * (index + 1);
        let x = 30;

        const { amount, currencySymbol } = getConvertedAmount(shipment);

        const rowData = [
          String(shipment?.trackingNumber ?? "N/A"),
        String(shipment?.senderAddress?.countryCode?.toUpperCase() ?? "N/A"),
        String(shipment?.destination?.address?.countryCode?.toUpperCase() ?? "N/A"),
        String(shipment?.shipmentDate ?? "N/A"),
        String(shipment?.carrierName?.toUpperCase() ?? "N/A"),
        String(shipment?.weight ?? "0"),
        ...(updated ? [String(shipment?.carrierWeight ?? "-")] : []),  // Carrier Weight will be included if updated is true
        String(shipment?.noOfPackages ?? "0"),
        `${currencySymbol} ${amount}`,
        String(shipment?.createdAt.split('T')[0] ?? "N/A"),
        ...(updated ? [] : [String(shipment?.createdBy ?? "N/A")]), // Carrier Weight if updated is true, else Created By
        ];

        rowData.forEach((cell, colIndex) => {
          currentPage.drawRectangle({
            x,
            y,
            width: columnWidths[colIndex],
            height: rowHeight,
            borderColor: rgb(0, 0, 0),
            borderWidth: 1,
          });
          currentPage.drawText(cell, { x: x + 5, y: y + 5, size: 8, font, color: rgb(0, 0, 0) });
          x += columnWidths[colIndex];
        });

        startY -= 0; // Update for next row
      });

      remainingShipments = remainingShipments.slice(16); // Remove shipments already added
    }

    // Save the PDF and open it in a new window
    const pdfBytes = await pdfDoc.save();
    const pdfUrl = URL.createObjectURL(new Blob([pdfBytes], { type: "application/pdf" }));
    window.open(pdfUrl, "_blank");
  } catch (error) {
    console.error("Error generating PDF:", error);
  }
};

export const showLoginError = (response) => {
  if (!response.dashboardAccess) {
    return 'You do not have access to the dashboard!';
  } else return response.message || 'Login failed. Please try again.'
}

export const getAvatarContent = (loggedInUser) => {
  if (!loggedInUser.trim()) return "";
  const nameParts = loggedInUser.trim().split(" ");
  return nameParts.length > 1
    ? (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase()
    : nameParts[0].charAt(0).toUpperCase();
};

export const getCarrierDetailsColumns = (userRole) => {
  const isAdmin = userRole === "admin";
  return [
    {
      field: 'id', headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>No </span>
        </>
      ), width: 70, align: 'left', headerAlign: 'left'
    },
    {
      field: 'name',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Carrier Version </span>
        </>
      ),
      width: 250,
      align: 'left',
      headerAlign: 'left',
      headerClassName: 'bold-header',
    },
    {
      field: 'trackingNumber',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Tracking Number </span>
        </>
      ), width: 180,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'customerPrice',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Customer Price  </span>  (we received)
        </>
      ),
      type: 'number',
      width: 250,
      align: 'left',
      headerAlign: 'left',
    },
    ...(isAdmin ? [{
      field: 'revenue',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Revenue  </span>  (we paid)
        </>
      ),
      type: 'number',
      width: 200,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'profit',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Profit </span>
        </>
      ),
      type: 'number',
      width: 180,
      align: 'left',
      headerAlign: 'left',
    }] : []),
    {
      field: 'from',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>From </span>
        </>
      ),
      type: 'number',
      width: 100,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'to',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>To </span>
        </>
      ),
      type: 'number',
      width: 100,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'totalPackagesWeight',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Total Weight (KG) </span>
        </>
      ),
      width: 150,
      align: 'left',
      headerAlign: 'left',
      headerClassName: 'bold-header',
    },
    {
      field: 'packagesCount',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>No. of Packages </span>
        </>
      ),
      width: 150,
      align: 'left',
      headerAlign: 'left',
      headerClassName: 'bold-header',
    },
    {
      field: 'shipmentDate',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Shipment Date </span>
        </>
      ),
      type: 'number',
      width: 150,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'createdAt',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Created At </span>
        </>
      ),
      type: 'number',
      width: 150,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'createdBy',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Created By </span>
        </>
      ),
      width: 150,
      align: 'left',
      headerAlign: 'left',
    },
  ];
}
export const getCarrierDetailsRows = (carrierDetails, userRole) => {
  const isAdmin = userRole === "admin";
  const rows = carrierDetails?.map((service, index) => {
    const originalCurrencyType = service?.shipmentCharges?.currencyType;
    const shipmentCreatedAt = service?.createdAt ? new Date(service.createdAt) : null;
    const thresholdDate = new Date("2025-02-01T00:00:00");
    let currencySymbol;
    if (originalCurrencyType === "BILLC") {
      if (shipmentCreatedAt >= thresholdDate) {
        currencySymbol = "$";
      } else currencySymbol = "€"
    } else {
      currencySymbol = (shipmentCreatedAt && shipmentCreatedAt >= thresholdDate)
        ? "$"
        : currencySymbolMap(originalCurrencyType);
    }

    return {
      id: index + 1,
      name: service?.carrierVersion?.toUpperCase(),
      trackingNumber: service?.trackingNumber || "-",
      ...(isAdmin && {
        revenue: service?.shipmentCharges?.carrierCost !== undefined
        ? `${currencySymbol || ""} ${formatNumberWithCommas(service.shipmentCharges.carrierCost)}`
        : "-",
      profit: service?.shipmentCharges?.customerPrice !== undefined &&
              service?.shipmentCharges?.carrierCost !== undefined
        ? `${currencySymbol || ""} ${formatNumberWithCommas(
            (service.shipmentCharges.customerPrice - service.shipmentCharges.carrierCost).toFixed(2)
          )}`
        : "-"
    }),
      customerPrice: service?.shipmentCharges?.customerPrice !== undefined
      ? `${currencySymbol || ""} ${formatNumberWithCommas(service.shipmentCharges.customerPrice)}`
      : "-",
      from: service?.from?.toUpperCase() || "-",
      to: service?.to?.toUpperCase() || "-",
      totalPackagesWeight: service?.totalPackagesWeight || "-",
      packagesCount: service?.packagesCount || "-",
      shipmentDate: service?.shipmentDate
        ? new Intl.DateTimeFormat("en-GB", {
          year: "numeric",
          month: "numeric",
          day: "numeric"
        }).format(new Date(service.shipmentDate))
        : "-",
      createdAtRaw: service?.createdAt ? new Date(service.createdAt) : null,
      createdAt: service?.createdAt
        ? new Intl.DateTimeFormat("en-GB", {
          year: "numeric",
          month: "numeric",
          day: "numeric"
        }).format(new Date(service.createdAt))
        : "-",
      createdBy: service?.createdBy || "-",
    };
  }) || [];

  return rows;
};

export const allCarrierColumns = (role, theme) => {
  const isAdmin = role === 'admin';
  return [
    {
      field: 'id',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>No </span>
        </>
      ),
      width: 90,
      align: 'left',
      headerAlign: 'left'
    },
    {
      field: 'name',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Carrier Name </span>
        </>
      ),
      width: 200,
      align: 'left',
      headerAlign: 'left',
      headerClassName: 'bold-header',
    },
    {
      field: 'sales',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Total Shipments </span>
        </>
      ),
      width: 160,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'totalCustomerPrice',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Total Customer Price  </span> (we received)
        </>
      ),
      type: 'number',
      width: 270,
      align: 'left',
      headerAlign: 'left',
    },
    ...(isAdmin
      ? [
        {
          field: 'totalRevenue',
          headerName: (
            <>
              <span style={{ fontWeight: 'bold' }}>Total Revenue</span> (we paid)
            </>
          ),
          type: 'number',
          width: 200,
          align: 'left',
          headerAlign: 'left',
        },
        {
          field: 'totalProfit',
          headerName: <span style={{ fontWeight: 'bold' }}>Total Profit</span>,
          type: 'number',
          width: 200,
          align: 'left',
          headerAlign: 'left',
        },
      ]
      : []),
    {
      field: 'totalPackagesWeight',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Total Weight (KG) </span>
        </>
      ),
      width: 150,
      align: 'left',
      headerAlign: 'left',
      headerClassName: 'bold-header',
    },
    {
      field: 'totalPackagesCount',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>No. of Packages </span>
        </>
      ),
      width: 150,
      align: 'left',
      headerAlign: 'left',
      headerClassName: 'bold-header',
    },
    {
      field: 'detail',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Details </span>
        </>
      ),
      width: 150,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        if (params.row.id === 'Total' || params.row.id === -1) {
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                paddingLeft: '10px',
              }}
            // className="bg-gray-300 font-bold" 
            >
            </div>
          );
        }
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              paddingLeft: '10px',
            }}
          >
            <div
              style={{
                width: 100,
                height: 30,
                backgroundColor: theme.palette.primary.main,
                borderRadius: '7px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Link
                to={`/carriers/details/${params.row.name}`}
                style={{
                  color: 'white',
                  fontSize: '0.85rem',
                  textAlign: 'center',
                  textDecoration: 'none',
                }}
              >
                View Details
              </Link>
            </div>
          </div>
        );
      },
    }
  ];
}


export const carrierVersionColumns = (role) => {
  const isAdmin = role === 'admin';
  return [
    {
      field: 'id',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>No </span>
        </>
      ),
      width: 90,
      align: 'left',
      headerAlign: 'left'
    },
    {
      field: 'name',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Carrier Version </span>
        </>
      ),
      width: 200,
      align: 'left',
      headerAlign: 'left',
      headerClassName: 'bold-header',
    },
    {
      field: 'trackingNumber',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Tracking Number </span>
        </>
      ),
      width: 150,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'customerPrice',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Customer Price  </span>  (we received)
        </>
      ),
      type: 'number',
      width: 220,
      align: 'left',
      headerAlign: 'left',
    },
    ...(isAdmin ? [{
      field: 'revenue',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Revenue  </span>  (we paid)
        </>
      ),
      type: 'number',
      width: 160,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'profit',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Profit </span>
        </>
      ),
      type: 'number',
      width: 120,
      align: 'left',
      headerAlign: 'left',
    }] : []),
    {
      field: 'from',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>From </span>
        </>
      ),
      type: 'number',
      width: 100,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'to',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>To </span>
        </>
      ),
      type: 'number',
      width: 100,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'totalPackagesWeight',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Total Weight (KG) </span>
        </>
      ),
      width: 150,
      align: 'left',
      headerAlign: 'left',
      headerClassName: 'bold-header',
    },
    {
      field: 'packagesCount',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>No. of Packages </span>
        </>
      ),
      width: 150,
      align: 'left',
      headerAlign: 'left',
      headerClassName: 'bold-header',
    },
    {
      field: 'shipmentDate',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Shipment Date </span>
        </>
      ),
      type: 'number',
      width: 150,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'createdAt',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Created At </span>
        </>
      ),
      type: 'number',
      width: 150,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'createdBy',
      headerName: (
        <>
          <span style={{ fontWeight: 'bold' }}>Created By </span>
        </>
      ),
      width: 150,
      align: 'left',
      headerAlign: 'left',
    },
  ];
}

const cleanValue = (value) => {
  if (typeof value !== 'string') return 0;

  const isEuro = value.includes('€');
  const numericValue = Number(value.replace(/[^\d.-]/g, '')) || 0;

  return isEuro ? numericValue * 1.09 : numericValue; // Convert € to $
};

export const calculateTotalsForCarrierVersions = (rows) => {
  let pageTotalCustomer = 0;
  let pageTotalRevenue = 0;
  let pageTotalProfit = 0;
  let overallTotalCustomer = 0;
  let overallTotalRevenue = 0;
  let overallTotalProfit = 0;

  const paginatedRows = rows.reduce((acc, row, index) => {
    const customerPrice = cleanValue(row.customerPrice);
    const revenue = cleanValue(row.revenue);
    const profit = cleanValue(row.profit);

    pageTotalCustomer += customerPrice;
    pageTotalRevenue += revenue;
    pageTotalProfit += profit;

    acc.push({ ...row });

    if ((index + 1) % 7 === 0 || index === rows.length - 1) {
      acc.push({
        id: `Total-${index + 1}`,
        name: "",
        customerPrice: `$ ${pageTotalCustomer.toFixed(2)}`,
        revenue: `$ ${pageTotalRevenue.toFixed(2)}`,
        profit: `$ ${pageTotalProfit.toFixed(2)}`,
      });

      overallTotalCustomer += pageTotalCustomer;
      overallTotalRevenue += pageTotalRevenue;
      overallTotalProfit += pageTotalProfit;

      pageTotalCustomer = 0;
      pageTotalRevenue = 0;
      pageTotalProfit = 0;
    }

    return acc;
  }, []);

  paginatedRows.push({
    id: 'Overall-Total',
    name: "All Carriers",
    customerPrice: `$ ${formatNumberWithCommas(overallTotalCustomer.toFixed(2))}`,
    revenue: `$ ${formatNumberWithCommas(overallTotalRevenue.toFixed(2))}`,
    profit: `$ ${formatNumberWithCommas(overallTotalProfit.toFixed(2))}`,
  });

  return paginatedRows;
};


export const calculateTotalsForCarrierDetails = (rows) => {
  let pageTotalCustomer = 0;
  let pageTotalRevenue = 0;
  let pageTotalProfit = 0;
  let overallTotalCustomer = 0;
  let overallTotalRevenue = 0;
  let overallTotalProfit = 0;

  const paginatedRows = rows.reduce((acc, row, index) => {

    const customerPrice = cleanValue(row.customerPrice) || 0;
    const revenue = cleanValue(row.revenue) || 0;
    const profit = cleanValue(row.profit) || 0;

    pageTotalCustomer += customerPrice;
    pageTotalRevenue += revenue;
    pageTotalProfit += profit;

    acc.push({ ...row });

    if ((index + 1) % 7 === 0 || index === rows.length - 1) {
      acc.push({
        id: `Total-${index + 1}`,
        name: "",
        customerPrice: `$ ${pageTotalCustomer.toFixed(2)}`,
        revenue: `$ ${pageTotalRevenue.toFixed(2)}`,
        profit: `$ ${pageTotalProfit.toFixed(2)}`,
      });

      overallTotalCustomer += pageTotalCustomer;
      overallTotalRevenue += pageTotalRevenue;
      overallTotalProfit += pageTotalProfit;

      // Reset page totals for the next batch
      pageTotalCustomer = 0;
      pageTotalRevenue = 0;
      pageTotalProfit = 0;
    }

    return acc;
  }, []);

  // Add overall totals at the end
  paginatedRows.push({
    id: 'Overall-Total',
    name: "All Carriers",
    customerPrice: `$ ${overallTotalCustomer.toFixed(2)}`,
    revenue: `$ ${overallTotalRevenue.toFixed(2)}`,
    profit: `$ ${overallTotalProfit.toFixed(2)}`,
  });

  return paginatedRows;
};

export const exportToExcel = (data, isAdmin, destination) => {
  const flatData = [];

  // let totalPackagesCount = 0;

  // data.forEach(item => {
  //   totalPackagesCount += item.packages?.length || 0;
  // });  
  // console.log('Total packages (CSV rows):', totalPackagesCount);

  data?.forEach((item) => {

    const carrier = item?.carrierVersion;
    const packages = item?.packages || []; 
    const charges = item?.shipmentCharges || [];  
    const sender = item?.senderAddress || [];  
    const receiver = item?.receiverAddress || []; 
    const shipmentDates = item?.shipmentDates || [];  
    const createdAtDates = item?.createdAtDates || [];  
    const tracking = item?.trackingNumber || [];  
    const packagesCountByTrackingNumber = item?.packagesCountByTrackingNumber || [];  

    packages.forEach((pkgArray, index) => {
      const pkg = pkgArray[0] || {}; 
      const charge = charges[index] || {};  
      const senderInfo = sender[index] || {};  
      const receiverInfo = receiver[index] || {};  
      const shipmentDate = shipmentDates?.[index] || ""; 
      const createdAt = createdAtDates?.[index] || "";  
      const trackingNumber = tracking?.[index] || "";  
      const packageCount =
        packagesCountByTrackingNumber.find(
          (item) => item.trackingNumber === trackingNumber
        )?.packagesLength || 0;

      flatData.push({
        Carrier: carrier?.toUpperCase() || "",
        "Tracking Number": trackingNumber || "",
        "Tracking URL": pkg?.trackingUrl || "",
        ...(isAdmin && {"Revenue (we paid)": charge?.carrierCost || ""}),
        "Customer Price": charge?.customerPrice || "",
        "Currency Type": charge?.currencyType || "",
        "Total Packages": packageCount,
        "Receiver Country": receiverInfo?.countryCode?.toUpperCase() || "",
        "Sender Country": senderInfo?.countryCode?.toUpperCase() || "",
        "Shipment Date": shipmentDate?.slice(0, 10),
        "Created At": createdAt?.slice(0, 10),
      });
    });
  });

  if (destination) {
    flatData.sort((a, b) => {
      const countryA = a["Receiver Country"];
      const countryB = b["Receiver Country"];
      if (countryA < countryB) {
        return -1;
      }
      if (countryA > countryB) {
        return 1;
      }
      return 0;
    });
  }

  const worksheet = XLSX.utils.json_to_sheet(flatData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Shipments");

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const dataBlob = new Blob([excelBuffer], {
    type: "application/octet-stream",
  });
  saveAs(dataBlob, "shipments_by_detailed.xlsx");
};

